import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { QuestionnaireRequest } from 'src/API';
import { Assessment } from 'src/app/core/api/assessment.service';
import { QuestionnaireRequestService } from 'src/app/core/api/questionnaire-request.service';

/** Inputs the linking process needs to persist a questionnaire↔assessment link. */
export interface LinkQuestionnaireContext {
  /** The assessment id, or 'new' when the assessment must be created first via `saveFn`. */
  assessmentId: string;
  /** The existing assessment to link to (ignored when `assessmentId === 'new'`). */
  assessment: Assessment;
  /** Creates and persists the assessment; only invoked when `assessmentId === 'new'`. */
  saveFn: () => Observable<Assessment>;
}

export interface LinkQuestionnaireOutcome {
  /** The persisted questionnaire request returned by the update mutation. */
  linkedQuestionnaire: QuestionnaireRequest;
  /** The assessment the questionnaire is now linked to (created when the id was 'new'). */
  assessment: Assessment;
}

/**
 * The questionnaire linking process: persists the link between a questionnaire request and an
 * assessment, creating the assessment first when it does not exist yet. Returns an Observable so the
 * modal owns its own view state (spinner/progress, newly-linked lists) and the process stays testable
 * with a spied `QuestionnaireRequestService` — no Angular TestBed required.
 */
@Injectable({ providedIn: 'root' })
export class LinkQuestionnaireService {
  constructor(private questionnaireRequestService: QuestionnaireRequestService) {}

  link(
    questionnaire: QuestionnaireRequest,
    context: LinkQuestionnaireContext
  ): Observable<LinkQuestionnaireOutcome> {
    const assessment$ =
      context.assessmentId === 'new' ? context.saveFn() : of(context.assessment);

    return assessment$.pipe(
      switchMap(assessmentData =>
        this.questionnaireRequestService
          .updateQuestionnaireRequest(
            {
              id: questionnaire.id,
              questionnaireRequestAssessmentId: assessmentData.id
            },
            false
          )
          .pipe(
            map(linkedQuestionnaire => {
              const otherLinkedQuestionnaires =
                assessmentData.questionnaireRequests &&
                assessmentData.questionnaireRequests.items
                  ? assessmentData.questionnaireRequests.items.filter(
                      existingQuestionnaire =>
                        existingQuestionnaire &&
                        existingQuestionnaire.id !== linkedQuestionnaire.id
                    )
                  : [];
              assessmentData.questionnaireRequests = {
                ...assessmentData.questionnaireRequests,
                items: [...otherLinkedQuestionnaires, linkedQuestionnaire]
              };
              questionnaire.assessment = assessmentData;
              return { linkedQuestionnaire, assessment: assessmentData };
            })
          )
      )
    );
  }
}
